// Utilitários do modelo "Campo" (topo 18 · busca 21 · card 15) — a linguagem de layout de um site imobiliário
// regional de referência (fundo de PAPEL creme, cartões quase-brancos, sans Inter pesada e apertada nos títulos,
// sobretítulo em versalete com fio à esquerda, tudo em PÍLULA — busca, botões, etiquetas —, cards de raio 20 e
// faixa de chamada em degradê suave) reproduzida em CSS modules NOSSOS, com as CORES DO TENANT. Nada de marca,
// texto, foto ou ícone da referência: o conteúdo é sempre o do tenant. Só as peças do Campo importam daqui — não é
// componente compartilhado dos ~300 sites. Reusa (importa, não copia) os helpers genéricos dos outros modelos.
import type { CSSProperties } from 'react';
import { getContrastRatio } from '../../../utils/FunctionsGlobals';
import { hexParaRgba, misturarCores } from '../horizonte/horizonte';
import { ajustarAte, hexValido, luminancia, serveDeBase } from '../larissa/larissa';

export { contatosDoTenant, hexParaRgba, linkWhatsApp, linkWhatsAppDoTenant, menuDoTenant, mesmaRota, misturarCores, redesDoTenant, useMovimentoReduzido } from '../horizonte/horizonte';
export type { ItemMenu, TelefoneTenant } from '../horizonte/horizonte';
export { fotoOtimizada, fotosDoImovel, nomeDaEmpresa, stripHtml, urlDoImovel } from '../atelier/atelier';
export { precosRotulados, semCaixaAlta, useLogoSemPlaca } from '../larissa/larissa';
export type { PrecoRotulado } from '../larissa/larissa';
export { areaFormatada, enderecoDoImovel, finalidadeDoImovel, semDuplicatas } from '../internacional/internacional';
export { atributosNorte as atributosCampo } from '../norte/norte';

// Curva das entradas do modelo (desacelera longo no fim) — a mesma dos modelos 8–13.
export const EASE_CAMPO = [0.22, 1, 0.36, 1] as const;

/** Inter (OFL, já auto-hospedada em /public/fonts/inter) — a mesma família da referência, em todos os pesos. */
export const SANS_CAMPO = "'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

/**
 * Escala de RESERVA = tokens MEDIDOS na referência (getComputedStyle 22/10/2026, /root/deploy-modelo14-22out/ref/):
 * papel do fundo (244,243,236), cartão (255,254,251), tinta dos títulos (35,39,31), texto de apoio (86,92,76),
 * placeholder/endereço (107,113,96), ocre do sobretítulo (138,101,31), verde da base e o verde fundo do destaque
 * do h1/preço. Entra só onde o tenant não tem cor utilizável.
 */
export const RESERVA_CP = {
    papel: '#f4f3ec',
    cartao: '#fffefb',
    tinta: '#23271f',
    apoio: '#565c4c',
    cinza: '#6b7160',
    ocre: '#8a651f',
    base: '#0f6b2c',
    branco: '#ffffff',
};

export interface PaletaCampo {
    /** cor de ação: botão de busca, pílulas sólidas, etiqueta "Venda", bolinha ativa (texto branco ≥ 5:1). */
    base: string;
    /** base mais funda: hover dos botões sólidos. */
    hover: string;
    /** destaque do h1, preço dos cards e ícones finos — base puxada para o escuro (≥ 7:1 sobre o papel). */
    profundo: string;
    /** acento quente: sobretítulo em versalete, fio do sobretítulo, traço sob o destaque do h1, "/ha" do preço. */
    acento: string;
    /** fundo da página (papel). */
    papel: string;
    /** cartões, caixa de busca, pílulas brancas. */
    cartao: string;
    /** títulos e texto forte. */
    tinta: string;
    /** subtítulos e parágrafos (≥ 4.5:1 sobre o papel). */
    apoio: string;
    /** placeholders, endereço e legendas (≥ 4.5:1 sobre o cartão). */
    cinza: string;
}

const primeiroQueLe = (partida: string, alvo: string, fundo: string, minimo: number): string => {
    for (let t = 0; t <= 1.0001; t += 0.05) {
        const c = t === 0 ? partida : misturarCores(partida, alvo, t);
        if (getContrastRatio(c, fundo) >= minimo) return c;
    }
    return alvo;
};

/**
 * Mapeamento p/ os 300 sites: base = a cor MAIS ESCURA do tenant que serve (saturada ou grafite; cor1 em empate),
 * escurecida até texto branco ler ≥ 5:1 (a referência mede ~6:1). profundo = base até ≥ 7:1 sobre o papel. acento
 * = cor2 do tenant quando é OUTRA cor utilizável (contraste ≥ 4.5:1 sobre o papel, ajustada se preciso); senão o
 * ocre medido. Neutros = os medidos com 3–6% da base. Sem cor no tenant → o verde/ocre da referência.
 */
export const paletaCampo = (site: any): PaletaCampo => {
    const cor1 = hexValido(site?.cor1);
    const cor2 = hexValido(site?.cor2);
    const bases = [cor1, cor2].filter(serveDeBase);
    const bruta = bases.length === 2 && luminancia(cor2) < luminancia(cor1) * 0.6 ? cor2 : bases[0] ?? '';
    const outra = bruta && bruta === cor2 ? cor1 : cor2;

    const pitada = (cor: string, t: number) => (bruta ? misturarCores(cor, bruta, t) : cor);
    const papel = pitada(RESERVA_CP.papel, 0.02);
    const cartao = RESERVA_CP.cartao;
    const base = ajustarAte(bruta || RESERVA_CP.base, '#000000', '#ffffff', 5);
    const hover = misturarCores(base, '#000000', 0.25);
    const profundo = primeiroQueLe(base, '#000000', papel, 7);
    const tinta = primeiroQueLe(pitada(RESERVA_CP.tinta, 0.05), '#000000', papel, 12);
    const apoio = primeiroQueLe(pitada(RESERVA_CP.apoio, 0.04), tinta, papel, 5.5);
    const cinza = primeiroQueLe(pitada(RESERVA_CP.cinza, 0.04), tinta, cartao, 4.5);
    const acento = outra && serveDeBase(outra) && outra.toLowerCase() !== (bruta || '').toLowerCase()
        ? primeiroQueLe(outra, '#000000', papel, 4.5)
        : RESERVA_CP.ocre;

    return { base, hover, profundo, acento, papel, cartao, tinta, apoio, cinza };
};

/** Variáveis CSS (--cp-*) que os módulos do Campo consomem; o Builder pode sobrescrevê-las por CSS custom. */
export const varsCampo = (site: any): CSSProperties => {
    const p = paletaCampo(site);
    const linha = (a: number) => hexParaRgba(p.tinta, a, 'rgba(35, 39, 31, ALPHA)');
    return {
        '--cp-base': p.base,
        '--cp-hover': p.hover,
        '--cp-profundo': p.profundo,
        '--cp-acento': p.acento,
        '--cp-papel': p.papel,
        '--cp-cartao': p.cartao,
        '--cp-tinta': p.tinta,
        '--cp-apoio': p.apoio,
        '--cp-cinza': p.cinza,
        '--cp-branco': RESERVA_CP.branco,
        // bordas medidas: 1px rgba(tinta, .10) nos cards/menu e .14 nas pílulas brancas
        '--cp-borda': linha(0.1),
        '--cp-borda-forte': linha(0.14),
        // fundos tingidos (ícone dos passos, chips): base a 8% e acento a 12% sobre o cartão
        '--cp-base-suave': misturarCores(p.cartao, p.base, 0.08),
        '--cp-acento-suave': misturarCores(p.cartao, p.acento, 0.14),
        // herói: papel com um véu claro da base embaixo; faixa do "Anuncie": acento → base, ambos bem claros
        '--cp-heroi': `linear-gradient(180deg, ${misturarCores(p.papel, '#ffffff', 0.5)} 0%, ${misturarCores(p.papel, p.base, 0.05)} 100%)`,
        '--cp-faixa': `linear-gradient(120deg, ${misturarCores(p.papel, p.acento, 0.14)} 0%, ${misturarCores(p.papel, p.base, 0.08)} 62%, ${misturarCores(p.papel, p.base, 0.11)} 100%)`,
        // sombras medidas: cartões quase sem sombra (borda faz o contorno); busca/destaque com sombra larga e macia
        '--cp-sombra': `0 1px 2px ${linha(0.04)}, 0 8px 24px -12px ${linha(0.16)}`,
        '--cp-sombra-alta': `0 24px 48px -20px ${linha(0.28)}`,
        '--cp-sans': SANS_CAMPO,
        // coluna medida: 1216px de conteúdo (112px de margem a 1440); 16px de lateral no celular
        '--cp-largura': '1216px',
        '--cp-lateral': '16px',
        '--cp-ease': 'cubic-bezier(0.22, 1, 0.36, 1)',
    } as CSSProperties;
};

/** Gate do modelo #14 "Campo" = topo 18 (mesmo critério de ehModeloNorte/ehModeloInternacional). */
export const ehModeloCampo = (site: any): boolean => Number(site?.modelo_topo) === 18;
